/**
 * Typed error produced when the client encounters an RFC 7807 problem-details response.
 * All network-level errors are converted to ApiError by the error middleware (see
 * ../middleware/error.ts). Apps never see raw Response objects — only ApiError.
 *
 * Use `ts-pattern` for exhaustive narrowing on errorCode:
 *
 *   import { match } from "ts-pattern";
 *   match(err)
 *     .with({ errorCode: "LEAVE_INSUFFICIENT_BALANCE" }, (e) => toast.error(e.title))
 *     .with({ errorCode: "EMPLOYEE_NOT_FOUND" }, (e) => redirect("/employees"))
 *     .exhaustive();  // compile error if any ErrorCode is unhandled
 */
import type { ErrorCode } from "./errorCodes";

export interface ApiErrorPayload {
  errorCode: ErrorCode;
  /** Pre-localized by backend per Accept-Language header. Safe to render directly. */
  title: string;
  status: number;
  /** Technical context — may contain unsafe text; gate behind admin-only UI. */
  detail?: string;
  /** URI of the resource that triggered the error. */
  instance?: string;
  /** Mirrors errorCode in dot-delimited form (e.g. "error.leave.balance_insufficient"). */
  messageKey?: string;
  /** ULID from the backend's X-Correlation-Id header. Log on every error. */
  correlationId: string;
  /**
   * Non-standard RFC 7807 members carried on the problem body beyond the
   * registered fields (type/title/status/detail/instance) and our copied
   * errorCode/correlationId/messageKey. Endpoint-specific extensions land here
   * verbatim — e.g. the 429 REPORT_QUOTA_EXHAUSTED variant's `retryAfter` +
   * `currentJobId` (see ReportQuotaExhaustedProblem in the generated schema).
   * Consumers narrow on errorCode before reading values out of this bag.
   */
  extensions?: Record<string, unknown>;
}

export class ApiError extends Error implements ApiErrorPayload {
  readonly errorCode: ErrorCode;
  override readonly name = "ApiError";
  readonly title: string;
  readonly status: number;
  readonly detail?: string;
  readonly instance?: string;
  readonly messageKey?: string;
  readonly correlationId: string;
  readonly extensions?: Record<string, unknown>;

  constructor(payload: ApiErrorPayload) {
    super(payload.title);
    this.errorCode = payload.errorCode;
    this.title = payload.title;
    this.status = payload.status;
    this.correlationId = payload.correlationId;
    if (payload.detail !== undefined) this.detail = payload.detail;
    if (payload.instance !== undefined) this.instance = payload.instance;
    if (payload.messageKey !== undefined) this.messageKey = payload.messageKey;
    if (payload.extensions !== undefined) this.extensions = payload.extensions;
  }

  /** Returns a plain object for Sentry extras / structured logging (omits stack). */
  toJSON(): ApiErrorPayload {
    const json: ApiErrorPayload = {
      errorCode: this.errorCode,
      title: this.title,
      status: this.status,
      correlationId: this.correlationId,
    };
    if (this.detail !== undefined) json.detail = this.detail;
    if (this.instance !== undefined) json.instance = this.instance;
    if (this.messageKey !== undefined) json.messageKey = this.messageKey;
    if (this.extensions !== undefined) json.extensions = this.extensions;
    return json;
  }
}

/** Narrowing helper — use in catch blocks before pattern-matching. */
export function isApiError(x: unknown): x is ApiError {
  return x instanceof ApiError;
}
